import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { API } from "@/lib/apiBase";
import { MathCaptcha, useCaptcha } from "@/components/MathCaptcha";
import { ImageUploadField } from "@/components/ImageUploadField";

const BLANK = {
  name: "", year: 2024, region: "", producer: "", vineyard: "",
  grape_variety: "", alcohol_pct: 13, size_ml: 750, best_drink: "",
  price: 0, market_value: 0, tasting_notes: "", wine_type: "red", image_url: null,
};

export default function SubmitWineDialog({ trigger, disabled = false, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const captcha = useCaptcha();

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        alcohol_pct: Number(form.alcohol_pct),
        size_ml: Number(form.size_ml),
        price: Number(form.price),
        market_value: Number(form.market_value),
        ...captcha.payload,
      };
      await axios.post(`${API}/wines/submit`, payload);
      toast.success("Wine submitted for admin review");
      setForm(BLANK);
      captcha.refresh();
      setOpen(false);
      onSubmitted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Submission failed. Check all fields.");
      captcha.refresh();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) captcha.refresh(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-wine-paper border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="submit-wine-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white/90">Submit a Wine</DialogTitle>
          <DialogDescription className="text-white/40 font-body text-sm">
            Your submission will be reviewed by an admin before appearing in the public catalog. Rating will be assigned later by a certified sommelier.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <F label="Name" value={form.name} onChange={(v) => setField("name", v)} required testId="sw-name" />
          <F label="Year" type="number" value={form.year} onChange={(v) => setField("year", v === "" ? "" : Number(v))} required testId="sw-year" />
          <F label="Region" value={form.region} onChange={(v) => setField("region", v)} required testId="sw-region" />
          <F label="Producer" value={form.producer} onChange={(v) => setField("producer", v)} required testId="sw-producer" />
          <F label="Vineyard" value={form.vineyard} onChange={(v) => setField("vineyard", v)} testId="sw-vineyard" />
          <F label="Grape Variety" value={form.grape_variety} onChange={(v) => setField("grape_variety", v)} required testId="sw-grape" />
          <F label="Alcohol %" type="number" step="0.1" value={form.alcohol_pct} onChange={(v) => setField("alcohol_pct", v === "" ? "" : Number(v))} testId="sw-alcohol" />
          <F label="Size (ml)" type="number" value={form.size_ml} onChange={(v) => setField("size_ml", v === "" ? "" : Number(v))} testId="sw-size" />
          <F label="Best Drink" value={form.best_drink} onChange={(v) => setField("best_drink", v)} placeholder="e.g. 2025-2040" testId="sw-bestdrink" />
          <div>
            <Label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">Wine Type</Label>
            <Select value={form.wine_type} onValueChange={(v) => setField("wine_type", v)}>
              <SelectTrigger data-testid="sw-type" className="bg-transparent border-white/10 text-white/80 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wine-paper border-white/10">
                <SelectItem value="red">Red</SelectItem>
                <SelectItem value="white">White</SelectItem>
                <SelectItem value="sparkling">Sparkling</SelectItem>
                <SelectItem value="rose">Rosé</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <F label="Price ($)" type="number" step="0.01" value={form.price} onChange={(v) => setField("price", v === "" ? "" : Number(v))} testId="sw-price" />
          <F label="Market Value ($)" type="number" step="0.01" value={form.market_value} onChange={(v) => setField("market_value", v === "" ? "" : Number(v))} testId="sw-mv" />
          <div className="md:col-span-2">
            <Label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">Tasting Notes</Label>
            <Textarea value={form.tasting_notes} onChange={(e) => setField("tasting_notes", e.target.value)} rows={3} data-testid="sw-notes" className="bg-transparent border-white/10 text-white/90 font-body" />
          </div>
          <ImageUploadField value={form.image_url} onChange={(v) => setField("image_url", v)} testPrefix="sw-image" />
          <MathCaptcha captcha={captcha} testPrefix="sw-captcha" className="md:col-span-2" />
          {error && <p className="md:col-span-2 text-sm text-red-400 font-body" data-testid="sw-error">{error}</p>}
          <DialogFooter className="md:col-span-2 gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-white/10 text-white/70" data-testid="sw-cancel">Cancel</Button>
            <Button type="submit" disabled={saving || disabled} className="bg-merlot hover:bg-merlot-light text-white" data-testid="sw-submit">
              {saving ? "Submitting..." : "Submit for Review"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, type = "text", step, placeholder, required, testId }) {
  return (
    <div>
      <Label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">{label}</Label>
      <Input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
        className="bg-transparent border-white/10 text-white/90 font-body h-10"
      />
    </div>
  );
}
